import { useState, useRef, useEffect } from 'react';
import { Settings, Mic, ArrowUp } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useAction } from '@pronteer/convex/react';
import { api } from '@pronteer/convex/api';
import type { Id } from '@pronteer/convex/dataModel';
import ReactMarkdown from 'react-markdown';
import {
  useAiChatSession,
  useCreateAiChatSession,
  useSendAiChatMessage,
} from '@/lib/convex-hooks';
import { useCurrentCrewMember } from '@/hooks/useCurrentCrewMember';
import { track, EVENTS } from '@/utils/analytics';
import { useKeyboardHeight } from '@/lib/keyboard';
import { cn } from '@/lib/utils';

// Get LightRAG URL from environment variable
const LIGHTRAG_URL = import.meta.env.VITE_LIGHTRAG_URL;

export function Assistant() {
  const { data: currentCrew } = useCurrentCrewMember();

  const [inputValue, setInputValue] = useState('');
  const [sessionId, setSessionId] = useState<Id<'aiChatSessions'> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Keyboard.resize is "none": the webview doesn't shrink for the keyboard, so
  // lift the composer above it ourselves (pb-24 clears the bottom nav, which
  // the keyboard covers while typing).
  const keyboardHeight = useKeyboardHeight();

  // Convex hooks
  const session = useAiChatSession(sessionId ?? undefined);
  const createSession = useCreateAiChatSession();
  const sendMessage = useSendAiChatMessage();
  const processQuery = useAction(api.aiChat.processQuery);

  // Get messages from session
  const messages = session?.messages ?? [];

  // Check if there's a pending/streaming message
  const isLoading = messages.some(
    (m) => m.role === 'assistant' && (m.status === 'pending' || m.status === 'streaming')
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async () => {
    if (!inputValue.trim() || isLoading || !currentCrew?.id || !currentCrew?.companyId) return;

    const question = inputValue.trim();
    setInputValue('');

    try {
      let currentSessionId = sessionId;

      // Create a new session if none exists
      if (!currentSessionId) {
        currentSessionId = await createSession({
          companyId: currentCrew.companyId,
          crewId: currentCrew.id,
        });
        setSessionId(currentSessionId);
      }

      // Send the message (creates user message + pending assistant message)
      const { assistantMessageId } = await sendMessage({
        sessionId: currentSessionId,
        content: question,
      });

      track(EVENTS.ASSISTANT_MESSAGE_SENT)

      // Process the query (calls LightRAG directly)
      await processQuery({
        sessionId: currentSessionId,
        assistantMessageId,
        question,
        lightragUrl: LIGHTRAG_URL,
      });
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const hasMessages = messages.length > 0;

  return (
    <div className="flex flex-col h-full bg-surface-L0 relative">
      {/* Gradient Background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(-10deg, var(--brand-accent) 9%, rgba(255, 255, 255, 0.6) 97%)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col h-full px-4 pt-4">
        {/* TopBar */}
        <div className="flex items-center justify-between shrink-0">
          <h1 className="text-xl font-semibold text-content-primary-bold">
            AI Assistant
          </h1>
          <Button
            variant="secondary"
            size="icon"
            className="w-8 h-8 rounded-full"
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>

        {/* Main Content */}
        {hasMessages ? (
          /* Conversation View */
          <div className="flex-1 overflow-y-auto overscroll-contain py-8 space-y-8">
            {messages.map((message) => (
              <div key={message._id}>
                {message.role === 'user' ? (
                  /* User Message */
                  <div className="flex flex-col items-end">
                    <div className="bg-background-brand-subtle rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl p-3 max-w-[273px]">
                      <p className="text-sm text-content-primary-bold leading-normal tracking-[0.07px]">
                        {message.content}
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Assistant Message */
                  <div className="flex flex-col gap-3 max-w-[273px]">
                    {message.status === 'pending' || message.status === 'streaming' ? (
                      /* Loading indicator */
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-background-brand-bold rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-background-brand-bold rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-background-brand-bold rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    ) : message.status === 'error' ? (
                      /* Error message */
                      <p className="text-sm text-content-negative-bold leading-normal tracking-[0.07px]">
                        {message.content || 'An error occurred. Please try again.'}
                      </p>
                    ) : (
                      /* Normal response */
                      <>
                        <div className="prose-assistant text-sm text-content-primary-bold leading-normal tracking-[0.07px]">
                          <ReactMarkdown
                            components={{
                              p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
                              strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                              ul: ({ children }) => <ul className="list-disc list-outside ml-4 mb-3 space-y-1">{children}</ul>,
                              ol: ({ children }) => <ol className="list-decimal list-outside ml-4 mb-3 space-y-1">{children}</ol>,
                              li: ({ children }) => <li className="pl-1">{children}</li>,
                              h1: ({ children }) => <h1 className="text-base font-semibold mb-2 mt-3 first:mt-0">{children}</h1>,
                              h2: ({ children }) => <h2 className="text-base font-semibold mb-2 mt-3 first:mt-0">{children}</h2>,
                              h3: ({ children }) => <h3 className="text-sm font-semibold mb-2 mt-3 first:mt-0">{children}</h3>,
                              a: ({ href, children }) => (
                                <a href={href} className="text-content-brand-bold underline" target="_blank" rel="noopener noreferrer">
                                  {children}
                                </a>
                              ),
                              code: ({ children }) => (
                                <code className="bg-surface-L2 px-1 py-0.5 rounded text-xs">{children}</code>
                              ),
                              hr: () => <hr className="my-3 border-border-L2" />,
                            }}
                          >
                            {message.content}
                          </ReactMarkdown>
                        </div>
                        {message.sources && message.sources.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1">
                            <Avatar className="w-6 h-6">
                              <AvatarImage src="" />
                              <AvatarFallback className="bg-background-brand-bold text-background text-[10px] font-medium">
                                AI
                              </AvatarFallback>
                            </Avatar>
                            {message.sources.map((source, idx) => (
                              <Badge
                                key={idx}
                                variant="secondary"
                                className="text-[10px] text-content-primary-subtle font-medium px-2 py-0.5 h-6 rounded-full"
                              >
                                {source.documentTitle}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        ) : (
          /* Getting Started View */
          <div className="flex-1 flex flex-col items-center justify-center px-9">
            <h2 className="text-xl font-semibold text-content-brand-bold tracking-[-0.4px] leading-[1.2]">
              How can I help you today?
            </h2>
            <p className="text-sm text-content-primary-subtle mt-2 text-center">
              Ask me about crew documents, passenger documents, or compliance information.
            </p>
          </div>
        )}

        {/* Input Section */}
        <div
          className={cn(
            'shrink-0 transition-[margin-bottom,padding-bottom] duration-300 ease-out',
            keyboardHeight > 0 ? 'pb-4' : 'pb-24',
          )}
          style={keyboardHeight > 0 ? { marginBottom: keyboardHeight } : undefined}
        >
          <Card>
            <div className="flex items-center justify-between gap-3">
              <Input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about PAX, crew, or documents..."
                disabled={isLoading}
                className="flex-1 border-0 bg-transparent shadow-none px-0 focus-visible:ring-0 focus-visible:border-0 dark:bg-transparent"
              />
              <div className="flex items-center gap-3">
                <Button
                  variant="secondary"
                  size="icon"
                  className="w-9 h-9 rounded-full min-w-9"
                  disabled={isLoading}
                >
                  <Mic className="w-4 h-4" />
                </Button>
                <Button
                  variant="primary"
                  size="icon"
                  className="w-9 h-9 rounded-full min-w-9 bg-background-brand-bold hover:bg-background-brand-bold/90"
                  onClick={handleSubmit}
                  disabled={isLoading || !inputValue.trim()}
                >
                  <ArrowUp className="w-4 h-4 text-white" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
